import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import ExpertSelect from '@components/fields/ExpertSelect';
import FormButtons from '@components/fields/FormButtons';
import Form from '@components/forms/Form';
import { ConsultReassignDocument, ConsultReassignMutation, ConsultReassignMutationVariables } from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  newExpertId: string | null;
};

type Consult = {
  expert: {
    fullName: string;
    id: string;
  };
  id: string;
  startTime: Date;
};

type Props = {
  consult: Consult;
  onCancel: () => void;
  onSuccess: () => void;
};

const ConsultReassignForm = ({ consult, onCancel, onSuccess }: Props) => {
  const initialValues = {
    newExpertId: null
  };
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      newExpertId: yup.string().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const [rescheduleConsult, { client }] = useMutation<ConsultReassignMutation, ConsultReassignMutationVariables>(
    ConsultReassignDocument,
    {
      update: () => {
        client.resetStore();
      }
    }
  );

  const onSubmit = useCallback(
    async () =>
      rescheduleConsult({
        variables: {
          id: consult.id,
          newExpertId: form.getValues().newExpertId as string
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.consultReassign,
          intl,
          onSuccess,
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.consult'
        });
      }),
    [consult.id, form, intl, onSuccess, rescheduleConsult]
  );

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <ExpertSelect<FormData>
        allowAny
        control={form.control}
        error={form.formState.errors.newExpertId}
        initialOption={{ label: consult.expert.fullName, value: consult.expert.id }}
        label={intl.formatMessage({ id: 'forms.consultReassign.newExpertLabel' })}
        name="newExpertId"
      />
      <FormButtons hookForm={form} onCancel={onCancel} />
    </Form>
  );
};

export default ConsultReassignForm;
